import { lazy, Suspense, useState } from 'react';
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router';
import { AuthProvider } from './auth/AuthContext';
import { RequireAuth } from './auth/RequireAuth';
import { AppLayout } from './components/AppLayout';
import { AiSettings } from './screens/ai/AiSettings';
import { BotScreen } from './screens/bot/BotScreen';
import { EmbedComms } from './embed/EmbedComms';
import { EmbedDashboard } from './embed/EmbedDashboard';
import { EmbedWebsite } from './embed/EmbedWebsite';
import { ChannelsHome } from './screens/channels/ChannelsHome';
import { CommsScreen } from './screens/comms/CommsModule';
import { ConnectForm } from './screens/connect/ConnectForm';
import { ConnectPicker } from './screens/connect/ConnectPicker';
import { DashboardRoute } from './screens/dashboard/DashboardSection';
import { HelpWidgetEmbed } from './helpwidget/HelpWidgetEmbed';
import { InboxManage } from './screens/inbox/InboxManage';
import { SignIn } from './screens/SignIn';
import { Sso } from './screens/Sso';

// Dev-only: the Communications module on fixture data, no session needed.
const CommsPreview = import.meta.env.DEV
  ? lazy(() =>
      import('./screens/comms/preview/CommsPreview').then((m) => ({ default: m.CommsPreview })),
    )
  : null;

// Dev-only: the Chatbot and AI config screens on fixture data — for design
// review and the handbook's screenshots, since the real routes need a session.
const ConfigPreview = import.meta.env.DEV
  ? lazy(() =>
      import('./screens/config-preview/ConfigPreview').then((m) => ({
        default: m.ConfigPreview,
      })),
    )
  : null;

// Dev-only: the help panel with a LimoSphere person in the conversation, on
// fixture data — those states otherwise need sphere-watch on the other end.
const HelpPreview = import.meta.env.DEV
  ? lazy(() => import('./helpchat/preview/HelpPreview').then((m) => ({ default: m.HelpPreview })))
  : null;

/**
 * Factory, not a module-level router: the router snapshots location when
 * created, so it must run AFTER captureOauthReturn has stripped ?oauth_error
 * from the address bar (main.tsx guarantees the order).
 */
function makeRouter() {
  return createBrowserRouter([
    { path: '/signin', element: <SignIn /> },
    // The dashboard's "open in a new tab" landing pad: swaps its one-time code
    // for a real session here, so nobody signs in twice. See auth/handoff.ts.
    { path: '/sso', element: <Sso /> },
    // Framed by the dispatch dashboard; the parent authenticates it over the
    // postMessage bridge, so it lives outside RequireAuth.
    { path: '/embed', element: <EmbedComms /> },
    // The operations dashboard, framed the same way and on its own bridge
    // prefix. A route rather than a tab on the module: this screen replaces the
    // dispatch dashboard's OWN /dashboard page, so it is opened by people who
    // may never touch Communications — and mounting it inside CommsModule would
    // put a socket and a conversation store behind the company's landing page.
    { path: '/dashboard-embed', element: <EmbedDashboard /> },
    // The dashboard's My website pages — the Chatbot and website Analytics —
    // framed on their own `CRM_SITE_` prefix. Pages of their own rather than
    // tabs on the Communications module: neither is a part of the inbox.
    { path: '/website-embed', element: <EmbedWebsite /> },
    // The help assistant the dashboard embeds where Chatwoot used to sit —
    // same bridge pattern, its own HELP_WIDGET_* protocol.
    { path: '/help-widget', element: <HelpWidgetEmbed /> },
    ...(CommsPreview
      ? [
          {
            path: '/comms-preview',
            element: (
              <Suspense fallback={null}>
                <CommsPreview />
              </Suspense>
            ),
          },
        ]
      : []),
    ...(HelpPreview
      ? [
          {
            path: '/help-preview',
            element: (
              <Suspense fallback={null}>
                <HelpPreview />
              </Suspense>
            ),
          },
        ]
      : []),
    ...(ConfigPreview
      ? [
          {
            path: '/config-preview',
            element: (
              <Suspense fallback={null}>
                <ConfigPreview />
              </Suspense>
            ),
          },
          {
            // The same preview, carrying the route param `?screen=inbox` needs:
            // InboxManage reads the channel off `:id`, and a preview at a path
            // with no param can only render it against `NaN`.
            path: '/config-preview/inboxes/:id',
            element: (
              <Suspense fallback={null}>
                <ConfigPreview />
              </Suspense>
            ),
          },
        ]
      : []),
    {
      element: <RequireAuth />,
      children: [
        // The product handbook. Signed-in but NOT permission-gated: it documents
        // the dispatch dashboard as well as the CRM, and any dashboard admin may
        // sign in here whether or not they hold a crm_* slug. It keeps its own
        // shell, so it sits beside AppLayout rather than inside it, and stays
        // lazy so the console never pays for it.
        { path: '/help', element: <Navigate to="/help/docs" replace /> },
        {
          path: '/help/docs',
          lazy: async () => ({
            Component: (await import('./screens/handbook/HandbookLayout')).HandbookLayout,
          }),
          children: [
            {
              index: true,
              lazy: async () => ({
                Component: (await import('./screens/handbook/HandbookHome')).HandbookHome,
              }),
            },
            {
              path: ':id',
              lazy: async () => ({
                Component: (await import('./screens/handbook/HandbookArticle')).HandbookArticle,
              }),
            },
          ],
        },
        {
          element: <AppLayout />,
          children: [
            { path: '/', element: <ChannelsHome /> },
            { path: '/comms', element: <CommsScreen /> },
            // dispatch's own operations metrics, drawn here. Gated server-side
            // on dispatch's `dashboard_all`, not on a CRM feature — the figures
            // are the company's, and dispatch already answers who may see them.
            // Website analytics is the section's second tab, offered only when
            // the agent's `analytics` feature is not off (DashboardSection).
            { path: '/dashboard', element: <DashboardRoute /> },
            { path: '/dashboard/analytics', element: <DashboardRoute /> },
            { path: '/connect', element: <ConnectPicker /> },
            { path: '/connect/:slug', element: <ConnectForm /> },
            { path: '/inboxes/:id', element: <InboxManage /> },
            { path: '/bot', element: <BotScreen /> },
            { path: '/ai', element: <AiSettings /> },
            // Analytics used to be a section of its own; links and bookmarks to
            // it still land, on the Dashboard tab it moved to.
            { path: '/analytics', element: <Navigate to="/dashboard/analytics" replace /> },
          ],
        },
      ],
    },
    { path: '*', element: <Navigate to="/" replace /> },
  ]);
}

export function App() {
  const [router] = useState(makeRouter);
  return (
    <AuthProvider>
      <RouterProvider router={router} />
    </AuthProvider>
  );
}
